// User feedback submission. Writes a row to public.feedback with optional
// screenshot in the feedback-screenshots Storage bucket, then notifies the
// /api/feedback-notify endpoint (which emails the founder via Resend if
// configured — best-effort, doesn't block).

import { supabase } from "./supabase";
import { apiFetch } from "./apiFetch";

export type FeedbackKind = "bug" | "suggestion" | "question" | "other";

export interface SubmitFeedbackInput {
  kind: FeedbackKind;
  body: string;
  screenshot?: File | null;
}

const SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024; // 5 MB — generous for an annotated screenshot

declare const __BUILD_ID__: string;

export async function submitFeedback({ kind, body, screenshot }: SubmitFeedbackInput): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");
  const trimmed = body.trim();
  if (!trimmed) throw new Error("Tell us what's on your mind.");
  if (trimmed.length > 4000) throw new Error("Please keep feedback under 4000 characters.");

  const {
    data: { user }
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Please sign in to send feedback.");

  // Build the feedback row first so we know the id (needed for the screenshot path).
  // We insert without screenshot_path, upload, then update — simpler than reserving
  // the id client-side.
  const insertPayload = {
    user_id: user.id,
    user_email: user.email ?? null,
    kind,
    body: trimmed,
    app_version: typeof __BUILD_ID__ !== "undefined" ? __BUILD_ID__ : null,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 512) : null,
    url: typeof window !== "undefined" ? window.location.pathname.slice(0, 1024) : null
  };

  const { data: row, error: insertError } = await supabase
    .from("feedback")
    .insert(insertPayload)
    .select("id")
    .single();

  if (insertError) throw insertError;
  if (!row) throw new Error("Couldn't save your feedback — try again.");

  let screenshotPath: string | null = null;

  if (screenshot) {
    if (screenshot.size > SCREENSHOT_MAX_BYTES) {
      // Don't fail the whole submit — the row is already saved. Just warn.
      console.warn("Screenshot >5MB, skipping upload");
    } else {
      const ext = pickExtension(screenshot);
      const path = `${user.id}/${row.id}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("feedback-screenshots")
        .upload(path, screenshot, {
          contentType: screenshot.type || "image/jpeg",
          cacheControl: "3600",
          upsert: false
        });
      if (uploadError) {
        console.warn("Screenshot upload failed", uploadError);
      } else {
        screenshotPath = path;
        // Update the row with the screenshot path so the notification email +
        // founder dashboard can find it.
        await supabase
          .from("feedback")
          .update({ screenshot_path: path })
          .eq("id", row.id);
      }
    }
  }

  // Fire-and-forget notify. The Pages Function emails the founder via Resend
  // if RESEND_API_KEY is configured. Failing here doesn't lose the feedback.
  void apiFetch("/api/feedback-notify", {
    method: "POST",
    body: JSON.stringify({ id: row.id, screenshot_path: screenshotPath })
  }).catch((err) => console.warn("feedback notify failed", err));
}

function pickExtension(file: File): string {
  const fromType = file.type.split("/")[1];
  if (fromType && /^(jpg|jpeg|png|gif|webp|heic)$/i.test(fromType)) return fromType.toLowerCase();
  const fromName = file.name.split(".").pop();
  if (fromName && fromName.length <= 5) return fromName.toLowerCase();
  return "jpg";
}
